import {useState, useCallback, useMemo, useContext, useEffect} from "react";
import {documentsAPI} from "../common/api/documentsAPI";
import {apartmentSearchAPI} from "../common/api/apartmentSearchAPI";
import {createURLQuery} from "../common/api/apiUtil";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {LocaleContext} from "../store/providers/LocaleProvider";
import {
    API_APARTMENT_SEARCH_RESORTS,
    API_CUSTOMER_DOCUMENTS_LIST,
    API_CUSTOMER_DOCUMENTS_LIST_ADMIN,
    API_CUSTOMER_DOCUMENT_SET_AS_READ,
    API_CUSTOMER_DOCUMENT_STREAM,
    API_CUSTOMER_DOCUMENT_TYPES_LIST,
} from "../common/api/apiConstants";

export const useCustomerDocuments = () => {
    const {localeUppercaseUnderscore} = useContext(LocaleContext)[0];
    const {initialized, authenticated, userId} = useKeycloakFunctions();

    const [customerDocuments, setCustomerDocuments] = useState();
    const [customerDocumentsTotal, setCustomerDocumentsTotal] = useState();
    const [loadingCustomerDocuments, setLoadingCustomerDocuments] = useState(false);
    const [errorCustomerDocuments, setErrorCustomerDocuments] = useState(null);

    const [customerDocument, setCustomerDocument] = useState(null);
    const [lastOpenedCustomerDocumentId, setLastOpenedCustomerDocumentId] = useState(null);
    const [loadingCustomerDocument, setLoadingCustomerDocument] = useState(false);
    const [errorCustomerDocument, setErrorCustomerDocument] = useState(null);

    const [documentTypes, setDocumentTypes] = useState({});

    const [readStatuses, setReadStatuses] = useState({});

    const [disableDocumentsForTrialsAndProspects, setDisableDocumentsForTrialsAndProspects] = useState(true);

    useEffect(() => {
        if (initialized && authenticated && !disableDocumentsForTrialsAndProspects) {
            getCustomerDocuments();
        }
    }, [disableDocumentsForTrialsAndProspects, initialized, authenticated]); // Will trigger on flag update

    const getCustomerDocuments = useCallback(async (params) => {
        if (initialized && authenticated) {
            if (disableDocumentsForTrialsAndProspects) {
                setErrorCustomerDocuments(true);
                setCustomerDocuments([]);
                setLoadingCustomerDocuments(false);
                return;
            }

            try {
                setErrorCustomerDocuments(false);
                setLoadingCustomerDocuments(true);

                const documentsApiUrl = params?.customer_id ? API_CUSTOMER_DOCUMENTS_LIST_ADMIN : API_CUSTOMER_DOCUMENTS_LIST;

                const documentsRes = await documentsAPI.get(documentsApiUrl + createURLQuery(params));
                let documents = documentsRes.data.documents;
                setCustomerDocumentsTotal(documentsRes.data.total_no_of_documents_found);
                if (documents?.length > 0) { //no need to call anything else if there are no documents
                    //Get the resorts, to match with corresponding_resort_id in documents
                    const resortsRes = await apartmentSearchAPI.get(API_APARTMENT_SEARCH_RESORTS + createURLQuery({locale: localeUppercaseUnderscore}));
                    const resortsArray = resortsRes.data;
                    const resortsById = {};
                    for (const res of resortsArray) {
                        resortsById[res.hapimag_resort_id] = res.resort_name;
                    }

                    // Get document types once
                    const docTypes = {};
                    if (!customerDocuments?.length) {
                        const documentTypesRes = await documentsAPI.get(API_CUSTOMER_DOCUMENT_TYPES_LIST + createURLQuery({locale: localeUppercaseUnderscore, include_blocked: true}));
                        for (const docType of documentTypesRes.data) {
                            docTypes[docType.id] = {
                                title: docType.title,
                                description: docType.description
                            };
                        }
                        setDocumentTypes(docTypes);
                    }
                    documents = documents.map(doc => {
                        try {
                            doc.title = docTypes[doc.document_type]?.title || documentTypes[doc.document_type]?.title || doc?.id;
                            if (doc?.corresponding_resort_id) {
                                doc.title += " " + resortsById[doc.corresponding_resort_id];
                            }
                        } catch (error) {
                            doc.title = doc?.id;
                            console.error(error);
                        }
                        return doc;
                    });
                    documents.sort((a, b) => {
                        return new Date(b.creation_date) - new Date(a.creation_date);
                    });
                    setCustomerDocuments(documents);
                    const rstatuses = {};
                    for (const doc of documents) {
                        rstatuses[doc.id] = !!doc.read;
                    }
                    setReadStatuses(rstatuses);
                } else {
                    setCustomerDocuments([]);
                    setReadStatuses({});
                }
            }
            catch (e) {
                setErrorCustomerDocuments(true);
            }
            finally {
                setLoadingCustomerDocuments(false);
            }
        }
    }, [initialized, authenticated, customerDocuments, disableDocumentsForTrialsAndProspects]);


    const getCustomerDocument = useCallback(async (params, customerId) => {
        if (initialized && authenticated) {
            try {
                setErrorCustomerDocument(false);
                setLoadingCustomerDocument(true);

                let customer_id;
                if (customerId) {
                    customer_id = customerId;
                } else {
                    customer_id = userId;
                }

                let apiUrl = API_CUSTOMER_DOCUMENTS_LIST_ADMIN + "/" + customer_id + API_CUSTOMER_DOCUMENT_STREAM + createURLQuery(params);


                const documentRes = await documentsAPI.get(apiUrl, {responseType: "blob"});
                setCustomerDocument(documentRes.data);
                setLastOpenedCustomerDocumentId(params?.document_id || params?.invoice_id);
            }
            catch (e) {
                setErrorCustomerDocument(e);
            }
            finally {
                setLoadingCustomerDocument(false);
            }
        }
    }, [initialized, authenticated]);


    const setCustomerDocumentAsRead = useCallback(async (documentId) => {
        if (initialized && authenticated) {
            try {
                await documentsAPI.post(API_CUSTOMER_DOCUMENTS_LIST + "/" + documentId + API_CUSTOMER_DOCUMENT_SET_AS_READ);
                setLastOpenedCustomerDocumentId(null);
                setReadStatuses({...readStatuses, [documentId]: true});
            }
            catch (e) {
                throw new Error("Could not mark as read: Document ID: " + documentId);
            }
        }

    }, [initialized, authenticated, readStatuses]);


    const memoedVal = useMemo(() => ({
        getCustomerDocuments,
        customerDocuments,
        customerDocumentsTotal,
        loadingCustomerDocuments,
        errorCustomerDocuments,

        readStatuses,

        getCustomerDocument,
        customerDocument,
        loadingCustomerDocument,
        errorCustomerDocument,
        lastOpenedCustomerDocumentId,

        setCustomerDocumentAsRead,

        setDisableDocumentsForTrialsAndProspects
    }), [
        getCustomerDocuments,
        customerDocuments,
        customerDocumentsTotal,
        loadingCustomerDocuments,
        errorCustomerDocuments,

        readStatuses,

        getCustomerDocument,
        customerDocument,
        loadingCustomerDocument,
        errorCustomerDocument,
        lastOpenedCustomerDocumentId,

        setCustomerDocumentAsRead,

        setDisableDocumentsForTrialsAndProspects
    ]);

    return memoedVal;
};
